<template>
    <div>
        <div class="mb-6 -mt-4">
            <h1>Financial Circumstances</h1>
        </div>

        <ValidationObserver ref="financialCircumstancesObserver">
            <div class="mb-6">
                <DropdownComponent
                    name="property_ownership"
                    :options="collections.residency"
                    label="Property Ownership"
                    placeholder="- Please Select -"
                    hint="Your net assets are calculated by subtracting the sum of your liabilities (eg, mortgages) from the sum of your total assets."
                    v-model="financialCircumstances.property_ownership"
                    @change="updateFinancialCircumstances"
                    :searchable="false"
                />
            </div>

            <div class="mb-6">
                <ScaleSliderComponent
                    :data="annualIncomeOptionsData"
                    value="annual_income"
                    label="What is your gross annual income from all sources?"
                    v-model="financialCircumstances.annual_income"
                    @change="updateFinancialCircumstances"
                    :hideMarks="false"
                    includePadding
                />
            </div>

            <div class="mb-6">
                <DropdownComponent
                    name="net_assets"
                    :options="collections.asset_values"
                    label="What are your net assets? (GBP or equivalent)"
                    placeholder="- Please Select -"
                    v-model="financialCircumstances.net_assets"
                    @change="updateFinancialCircumstances"
                    hint="Net investible assets is the cash that you have available to invest or that is already invested. It excludes property and pensions."
                    :searchable="false"
                />
            </div>

            <div class="mb-6 flex">
                <div class="w-1/2 mr-2">
                    <NumberInputComponent
                        label="Initial Deposit (GBP or equivalent - £10,000 min)"
                        placeholder="Minimum 10,000 GBP"
                        name="initial_deposit"
                        rules="required|positive|min_value:10000"
                        :step="1000"
                        hint="This would represent the initial investment amount you intend to deposit with us."
                        v-model="financialCircumstances.initial_deposit"
                        @change="updateFinancialCircumstances"
                    />
                </div>

                <div class="w-1/2 ml-2">
                    <NumberInputComponent
                        label="Total Risk Capital (GBP or equivalent)"
                        placeholder="Total Risk Capital"
                        name="total_risk_capital"
                        rules="required|positive"
                        :step="1000"
                        hint="This would represent the total of all of your capital as a total sum that you are prepared to risk."
                        v-model="financialCircumstances.total_risk_capital"
                        @change="updateFinancialCircumstances"
                    />
                </div>
            </div>

            <div class="mb-6">
                <h2>
                    In order to establish whether you can be classed as a professional investor, please confirm whether
                    you are any two of the following:
                </h2>
            </div>

            <div class="mb-6">
                <YesNoSliderComponent
                    name="financial_investment_portfolio"
                    label="Is your financial investment portfolio greater than 500,000 GBP?"
                    v-model="financialCircumstances.financial_investment_portfolio"
                    @change="updateFinancialCircumstances"
                />
            </div>

            <div class="mb-6">
                <YesNoSliderComponent
                    name="worked_in_financial_sector"
                    label="You work or have worked in the financial sector for at least one year in a professional position, which requires knowledge of the transactions or services envisaged;"
                    v-model="financialCircumstances.worked_in_financial_sector"
                    @change="updateFinancialCircumstances"
                />
            </div>

            <div class="mb-6">
                <YesNoSliderComponent
                    name="transactions_average_frequency"
                    label="You have carried out transactions, in significant size, on the relevant market at an average frequency of 10 per quarter over the previous four quarters;"
                    v-model="financialCircumstances.transactions_average_frequency"
                    @change="updateFinancialCircumstances"
                />
            </div>

            <transition
                enter-active-class="transition ease-out duration-500 transform"
                enter-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="transition ease-out duration-200 transform"
                leave-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <div v-show="isClassedAsProfessional" class="mb-6">
                    <YesNoQuestionComponent
                        name="opt_in_as_professional_customer"
                        label='As you have indicated above that you could be classified as a professional customer, please confirm whether you would like to opt up to professional status? <a href="https://www.handbook.fca.org.uk/handbook/COBS/3/5.html" target="_blank" rel="noopener noreferrer">Further information on Professional classification</a>'
                        v-model="financialCircumstances.opt_in_as_professional_customer"
                        @change="
                            updateFinancialCircumstances();
                            resetClientAgreementConfirmation();
                        "
                        rules="optional"
                    />
                </div>
            </transition>

            <div class="mb-6">
                <ConfirmationComponent
                    name="confirm_small_percentage_of_net_worth"
                    label="Confirm"
                    text="Please confirm that, given the risk of loss of capital in trading futures and options (derivatives) and equities, any risk capital traded through us should only represent a small percentage of your overall net worth."
                    v-model="financialCircumstances.confirm_small_percentage_of_net_worth"
                    @change="updateFinancialCircumstances"
                />
            </div>
        </ValidationObserver>
    </div>
</template>

<script>
import { ValidationObserver } from "vee-validate";
import { mapGetters, mapActions } from "vuex";
import stepperValidationService from "@services/stepperValidationService";

export default {
    name: "FinancialCircumstancesFormComponent",
    props: {},
    components: {
        ValidationObserver,
    },
    props: {
        step: { default: "" },
    },
    computed: {
        ...mapGetters(["collections", "financialCircumstancesState"]),
        isClassedAsProfessional() {
            // Calculates if the user could potentially be classed as a professional and should therefore show conditional form fields
            return (
                this.financialCircumstances.financial_investment_portfolio +
                    this.financialCircumstances.worked_in_financial_sector +
                    this.financialCircumstances.transactions_average_frequency >
                1
            );
        },
    },
    data: function () {
        return {
            financialCircumstances: {},
            isFormValidated: false,
            annualIncomeOptionsData: [
                {
                    value: "0-£50k",
                    label: "0-£50k",
                },
                {
                    value: "£51-£100k",
                    label: "£51-£100k",
                },
                {
                    value: "£101-£249k",
                    label: "£101-£249k",
                },
                {
                    value: ">£250k",
                    label: ">£250k",
                },
            ],
        };
    },
    methods: {
        ...mapActions([
            "doGetFinancialCircumstances",
            "doUpdateFinancialCircumstances",
            "doGetCollectionData",
            "doResetClientAgreementConfirmation",
        ]),
        updateFinancialCircumstances() {
            this.doUpdateFinancialCircumstances(this.financialCircumstances).then(
                (response) => (this.financialCircumstances = response)
            );
        },
        doValidateForm() {
            stepperValidationService.checkStepperStepStatus(
                this.step,
                this.financialCircumstances,
                this.$refs.financialCircumstancesObserver,
                false,
                true
            );
        },
        resetClientAgreementConfirmation() {
            this.doResetClientAgreementConfirmation();
        },
    },
    mounted() {
        this.$root.$on("submittingApplication", () => this.doValidateForm());
        this.$root.$on("stepChanged", (step) => {
            if (this.step < step && !this.isFormValidated) {
                this.doValidateForm();
                this.isFormValidated = true;
            } else if (this.step == step) {
                this.isFormValidated = false;
            }
        });

        this.doGetFinancialCircumstances().then((response) => (this.financialCircumstances = response));
        this.doGetCollectionData("residency");
        this.doGetCollectionData("asset_values");

        this.$gtm.trackView('Financial Circumstances', 'currentPath');
    },
    watch: {
        financialCircumstances() {
            stepperValidationService.checkStepperStepStatus(
                this.step,
                this.financialCircumstances,
                this.$refs.financialCircumstancesObserver
            );
        },
        "financialCircumstances.initial_deposit"() {
            if (this.financialCircumstances.initial_deposit) {
                this.financialCircumstances.initial_deposit = Number(
                    this.financialCircumstances.initial_deposit.replace(/[^0-9\.-]+/g, "")
                ).toLocaleString();
            }
        },
        "financialCircumstances.total_risk_capital"() {
            if (this.financialCircumstances.total_risk_capital) {
                this.financialCircumstances.total_risk_capital = Number(
                    this.financialCircumstances.total_risk_capital.replace(/[^0-9\.-]+/g, "")
                ).toLocaleString();
            }
        },
    },
};
</script>
